import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { ReportWidget } from '../types/visualization';

export const useWidgetExecution = () => {
  const [executingWidgets, setExecutingWidgets] = useState<Set<number>>(new Set());
  const [widgetData, setWidgetData] = useState<Map<number, any[]>>(new Map());

  // Execute widget SQL and store results
  const executeWidget = useCallback(async (widget: ReportWidget) => {
    setExecutingWidgets(prev => new Set(prev).add(widget.id));
    
    try {
      // Send parameters as direct properties to match backend expectations
      const result = await apiService.executeSQL(widget.sql_query, {
        // Send chart type and config as direct properties (not nested)
        chartType: widget.visualization_type || widget.chartType,
        config: widget.config,
        metadata: widget.metadata,
        title: widget.name
      });

      // Store the data
      setWidgetData(prev => new Map(prev).set(widget.id, result.results));

      return result;
    } catch (error) {
      console.error('❌ Error executing widget:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to execute widget "${widget.name}": ${errorMessage}`);
    } finally {
      setExecutingWidgets(prev => {
        const newSet = new Set(prev);
        newSet.delete(widget.id);
        return newSet;
      });
    }
  }, []);

  // Execute multiple widgets
  const executeWidgets = useCallback(async (widgets: ReportWidget[]) => {
    // Execute all widgets in parallel
    const executions = widgets.map(widget => {
      return executeWidget(widget).catch(error => {
        console.error(`Failed to execute widget ${widget.name}:`, error);
        return null;
      });
    });

    await Promise.allSettled(executions);
  }, [executeWidget]);

  // Get widget data
  const getWidgetData = useCallback((widgetId: number) => {
    return widgetData.get(widgetId);
  }, [widgetData]);

  // Check if widget is executing
  const isWidgetExecuting = useCallback((widgetId: number) => {
    return executingWidgets.has(widgetId);
  }, [executingWidgets]);

  // Clear widget data
  const clearWidgetData = useCallback((widgetId: number) => {
    setWidgetData(prev => {
      const newMap = new Map(prev);
      newMap.delete(widgetId);
      return newMap;
    });
  }, []);

  return {
    executeWidget,
    executeWidgets,
    getWidgetData,
    isWidgetExecuting,
    clearWidgetData,
    executingWidgets,
    widgetData,
  };
};
